import CalendarIcon from "../../../assets/svg/calendar-icon";
import ClockIcon from "../../../assets/svg/clock-icon";
import CompletedIcon from "../../../assets/svg/completed-icon";
import FinanceIcon from "../../../assets/svg/finance-icon";
import InboundIcon from "../../../assets/svg/inbound-icon";
import MaintenanceIcon from "../../../assets/svg/maintenance-icon";
import MarketingIcon from "../../../assets/svg/marketing-icon";
import OperationsIcon from "../../../assets/svg/operations-icon";
import OutboundIcon from "../../../assets/svg/outbound-icon";
import SalesIcon from "../../../assets/svg/sales-icon";
import DollarIcon from "../../../assets/svg/dollar-icon";
import PoundIcon from "../../../assets/svg/pound-icon";
import { getClientCRMFlag } from "../../../utils/constants/clientCustomCRMFlags";
import { showsLast6MonthsOption } from "../../../utils/constants/durationDefaults";

import {
  getStatusIcon,
  getStatusColor,
} from "../../../pages/Campaigns/components/CampaignTable/utils/functions";
import { WORKFLO_NAME } from "../../../utils/constants/globalConstants";
import { BudgetDuration, Currency } from "../types";
import CommunicationAllIcon from "../../../assets/svg/communication-all-icon";
import { getRegionLabel } from "../../../utils/constants/mappings";

export enum CampaignStatus {
  RUNNING = "running",
  SCHEDULED = "scheduled",
  COMPLETED = "completed",
  DRAFT = "draft",
  FAILED = "failed",
  TERMINATED = "terminated",
  RETRY_IN_PROGRESS = "retry_in_progress",
}

export const STATUS_OPTIONS = [
  {
    value: CampaignStatus.RUNNING,
    displayValue: "Running",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.RUNNING)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.RUNNING)}>Running</h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.SCHEDULED,
    displayValue: "Scheduled",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.SCHEDULED)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.SCHEDULED)}>
            Scheduled
          </h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.COMPLETED,
    displayValue: "Completed",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.COMPLETED)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.COMPLETED)}>
            Completed
          </h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.DRAFT,
    displayValue: "Draft",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.DRAFT)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.DRAFT)}>Draft</h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.FAILED,
    displayValue: "Failed",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.FAILED)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.FAILED)}>Failed</h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.TERMINATED,
    displayValue: "Terminated",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.TERMINATED)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.TERMINATED)}>
            Terminated
          </h3>
        </div>
      </div>
    ),
  },
  {
    value: CampaignStatus.RETRY_IN_PROGRESS,
    displayValue: "Retry in Progress",
    content: (
      <div className="flex items-center gap-1">
        {getStatusIcon(CampaignStatus.RETRY_IN_PROGRESS)}
        <div>
          <h3 className={getStatusColor(CampaignStatus.RETRY_IN_PROGRESS)}>
            Retrying
          </h3>
        </div>
      </div>
    ),
  },
];

// Order here (Inbound, Outbound, All) is shared with Billing/CRM/CommonFilters — those pages still
// default to "inbound" and are unaffected by the Analytics board's default flipping to "all" (see
// AnalyticsCommunicationTypeFilter.tsx, which reorders its OWN rendering of this list rather than
// reordering it here for every consumer).
export const COMMUNICATION_TYPE_OPTIONS = [
  {
    value: "inbound",
    displayValue: "Inbound",
    content: (
      <div className="flex items-center gap-[10px]">
        <InboundIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Inbound
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Message received from users
          </p>
        </div>
      </div>
    ),
  },

  {
    value: "outbound",
    displayValue: "Outbound",
    content: (
      <div className="flex items-center gap-[10px]">
        <OutboundIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Outbound
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Message sent to users by verbaflo
          </p>
        </div>
      </div>
    ),
  },
  {
    value: "all",
    displayValue: "All",
    content: (
      <div className="flex items-center gap-[10px]">
        <CommunicationAllIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk  " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            All Communications
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Message received and sent by VerbaFlo
          </p>
        </div>
      </div>
    ),
  },
];

export const getCampaignCategoryOptions = (clientCode?: string) => ({
  sales: {
    value: "sales",
    displayValue: getRegionLabel("sales", clientCode),
    content: (
      <div className="flex items-center gap-[10px]">
        <SalesIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            {getRegionLabel("sales", clientCode)}
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Data-driven sales {WORKFLO_NAME.singular} for maximum impact.
          </p>
        </div>
      </div>
    ),
  },
  marketing: {
    value: "marketing",
    displayValue: "Marketing",
    content: (
      <div className="flex items-center gap-[10px]">
        <MarketingIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Marketing
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            AI-powered marketing {WORKFLO_NAME.singular}
          </p>
        </div>
      </div>
    ),
  },
  finance: {
    value: "finance",
    displayValue: "Finance",
    content: (
      <div className="flex items-center gap-[10px]">
        <FinanceIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Finance
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Optimize occupancy revenue with smarter forecasting.
          </p>
        </div>
      </div>
    ),
  },
  operations: {
    value: "operations",
    displayValue: "Operations",
    content: (
      <div className="flex items-center gap-[10px]">
        <OperationsIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Operations
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Automate resident services and streamline daily tasks.
          </p>
        </div>
      </div>
    ),
  },
  maintenance: {
    value: "maintenance",
    displayValue: "Maintenance",
    content: (
      <div className="flex items-center gap-[10px]">
        <MaintenanceIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk " />
        <div>
          <h3 className="text-md font-medium text-heading-text dark:text-heading-text-dk">
            Maintenance
          </h3>
          <p className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk">
            Streamline repairs and keep properties in top condition.
          </p>
        </div>
      </div>
    ),
  },
});

export const DURATION_OPTIONS = [
  {
    icon: (
      <ClockIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "last-24-hours",
    content: "Last 24 Hours",
  },
  {
    icon: (
      <ClockIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "last-7-days",
    content: "Last 7 Days",
  },
  {
    icon: (
      <ClockIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "last-30-days",
    content: "Last 30 Days",
  },
  {
    icon: (
      <ClockIcon className="w-[16px] h-[16px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "last-60-days",
    content: "Last 60 Days",
  },
  {
    icon: (
      <CalendarIcon className="w-[20px] h-[20px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "last-month",
    content: "Last Month",
  },
  {
    icon: (
      <CalendarIcon className="w-[20px] h-[20px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "this-month",
    content: "This Month",
  },
  {
    icon: (
      <CalendarIcon className="w-[20px] h-[20px] text-gray-placeholder dark:text-gray-placeholder-dk" />
    ),
    value: "custom",
    content: "Custom",
  },
];

/**
 * Duration presets for the pickers. "Last 6 Months" is only listed for the tenants flagged for it
 * (see utils/constants/durationDefaults.ts) — everyone else gets the plain list.
 */
export const getDurationOptions = () =>
  showsLast6MonthsOption()
    ? [
        ...DURATION_OPTIONS.slice(0, 4),
        {
          icon: (
            <CalendarIcon className="w-[20px] h-[20px] text-gray-placeholder dark:text-gray-placeholder-dk" />
          ),
          value: "last-6-months",
          content: "Last 6 Months",
        },
        ...DURATION_OPTIONS.slice(4),
      ]
    : DURATION_OPTIONS;

export const getModalityDisplayValue = (value: string) => {
  if (value === "all" || value === "all_channels") return "All Channels";
  if (value === "widget") return "VerbaChat";
  if (value === "whatsapp") return "VerbaMessenger";
  if (value === "email") return "VerbaMail";
  if (value === "call") return "VerbaCall";
  if (value === "sms") return "VerbaSMS";
  if (value === "fb_messenger") return "VerbaFbMessenger";
  if (value === "instagram") return "VerbaInstagram";
  if (value === "wechat") return "VerbaWeChat";
  return value;
};

export const getRedirectionCampignValue = (value: string) => {
  if (value === "all" || value === "all_channels") return "All Channels";
  if (value === "widget") return "verbachat";
  if (value === "whatsapp") return "verbamessenger";
  if (value === "email") return "verbamail";
  if (value === "call") return "verbacall";
  if (value === "sms") return "verbasms";
  if (value === "fb_messenger") return "fbmessenger";
  if (value === "instagram") return "instagram";
  if (value === "wechat") return "wechat";
  return value;
};

export const STATUS_MAP_FOR_CHANNEL = {
  call: ["callbacks_received", "calls_answered"],
  email: ["emails_replied"],
  whatsapp: ["messages_replied"],
};

export const EXECUTION_LOGS_STATUS_OPTIONS = [
  {
    value: "failed",
    content: "Failed",
  },
  {
    value: "lead_updated",
    content: "Lead Updated",
  },
  {
    value: "notification_sent",
    content: "Notification Sent",
  },
  {
    value: "message_replied",
    content: "Message Replied",
  },
  {
    value: "voicemail",
    content: "Voicemail",
  },
];

export const ACADEMIC_YEAR_OPTIONS = [
  { value: "2023/24", content: "2023/24" },
  { value: "2024/25", content: "2024/25" },
  { value: "2025/26", content: "2025/26" },
  { value: "2026/27", content: "2026/27" },
  { value: "2027/28", content: "2027/28" },
  { value: "2028/29", content: "2028/29" },
  { value: "2029/30", content: "2029/30" },
  { value: "2030/31", content: "2030/31" },
  { value: "2031/32", content: "2031/32" },
  { value: "2032/33", content: "2032/33" },
  { value: "2033/34", content: "2033/34" },
  { value: "BTR", content: "BTR" },
  { value: "Summer 2024", content: "Summer 2024" },
  { value: "Summer 2025", content: "Summer 2025" },
  { value: "Summer 2026", content: "Summer 2026" },
];

export const ROOM_TYPE_OPTIONS = [
  {
    value: "non_ensuite",
    content: "Non-Ensuite",
  },
  {
    value: "ensuite",
    content: "Ensuite",
  },
  {
    value: "shared",
    content: "Shared",
  },
  {
    value: "studio",
    content: "Studio",
  },
  {
    value: "apartment",
    content: "Apartment",
  },
  {
    value: "penthouse",
    content: "Penthouse",
  },
  {
    value: "enhanced",
    content: "Enhanced",
  },
  {
    value: "elite",
    content: "Elite",
  },
  {
    value: "duo",
    content: "Duo",
  },
  {
    value: "essential",
    content: "Essential",
  },
  {
    value: "essential_extra",
    content: "Essential Extra",
  },
];

export const LEAD_TYPE_OPTIONS = [
  {
    value: "working professional",
    content: "Professional",
  },
  {
    value: "student",
    content: "Student",
  },
];

export const getDynamicFilterLabels = (clientRegion?: string) => ({
  communication: {
    budget: {
      label: clientRegion === "US" ? "Monthly Budget" : "Budget per week",
      icon:
        clientRegion === "US" ? (
          <DollarIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
        ) : (
          <PoundIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
        ),
    },
  },
});

export const FILTER_LABELS = {
  communication: {
    modeOfCommunication: {
      label: "Mode of Communication",
      description: "Select your preferred channel of communication",
    },
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
    sourceCountry: {
      label: "Source Country",
      description: "Select the source country",
    },
    destinationCountry: {
      label: "Destination Country",
      description: "Select the destination country",
    },
    destinationCity: {
      label: "Destination City",
      description: "Select the  city",
    },
    destinationUniversity: {
      label: "Destination University",
      description: "Select the university",
    },
    tags: {
      label: "Tags",
      description: "Select the tags",
    },
    roomType: {
      label: "Room Type",
      description: "Select the room type",
    },
    budget: {
      label: "Budget",
      description: "Select the budget",
    },
    property: {
      label: "Property",
      description: "Select the property",
    },
    utmSources: {
      label: "UTM Sources",
      description: "",
    },
    utmMedium: {
      label: "UTM Medium",
      description: "",
    },
    attributionChannel: {
      label: "Attribution Channel",
      description: "",
    },
    standardMedium: {
      label: "Standard Medium",
      description: "",
    },
    standardSource: {
      label: "Standard Source",
      description: "",
    },
    deviceType: {
      label: "Device Type",
      description: "",
    },
    leaseDuration: {
      label: "Lease Duration",
      description: "",
    },
    assignedAgents: {
      label: "Assigned Agents",
      description: "Select the assigned agents",
    },
    leadType: {
      label: "Lead Type",
      description: "Select the lead type",
    },
    viewings: {
      label: "Viewings",
      description: "",
    },
    excludeRepeatLeads: {
      label: "Exclude Repeat Leads",
      description: "",
    },
    chatbotVersion: {
      label: "Chatbot Version",
      description:
        "Show only chats handled by a specific chatbot version. Leave empty to include every version.",
    },
    visitorPresence: {
      label: "VerbaChat Visitor Presence",
      description:
        "Show VerbaChat conversations by whether the visitor is still on the widget right now.",
    },
  },
  campaignCommunications: {
    modeOfCommunication: {
      label: "Mode of Communication",
      description: "Select your preferred mode of communication",
    },
    status: {
      label: "Status",
      description: "Select the status",
    },
    outcome: {
      label: "Outcome",
      description: "Select the outcome",
    },
  },
  campaigns: {
    categories: {
      label: "Categories",
      description: "Select the categories",
    },
    useCase: {
      label: "Use Case",
      description: "Select use cases",
    },
    branch: {
      label: "Branch",
      description: "Select branch",
    },
    createdBy: {
      label: "Created By",
      description: "Select user",
    },
    status: {
      label: "Status",
      description: "Select the status",
    },
    startsOnDate: {
      label: "Starts on Date",
      description: "",
    },
    createdOnDate: {
      label: "Created on Date",
      description: "",
    },
  },
  campaignTemplates: {
    createdOnDate: {
      label: "Created on Date",
      description: "",
    },
  },
  campaignLogs: {
    selectUser: {
      label: "Select User",
      description: "",
    },
  },
  campaignExecutionLogs: {
    modeOfCommunication: {
      label: "Mode of Communication",
      description: "",
    },
    dateRange: {
      label: "Date Range",
      description: "",
    },
    status: {
      label: "Status",
      description: "Select the status",
    },
  },
  analytics: {
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
    typeOfCommunication: {
      label: "Type of Communication",
      description: "Select your preferred type of communication",
    },
    modality: {
      label: "Modality",
      description: "Select which channel's data this board shows",
    },
  },
  billing: {
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
    typeOfCommunication: {
      label: "Type of Communication",
      description: "Select your preferred type of communication",
    },
  },
  crm: {
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
    typeOfCommunication: {
      label: "Type of Communication",
      description: "Select your preferred type of communication",
    },
    sourceCountry: {
      label: "Source Country",
      description: "Select the source country",
    },
    destinationCountry: {
      label: "Destination Country",
      description: "Select the destination country",
    },
    destinationCity: {
      label: "Destination City",
      description: "Select the destination city",
    },
    university: {
      label: "University",
      description: "Select the university",
    },
    assignedAgents: {
      label: "Assigned Agents",
      description: "Select the assigned agents",
    },
    tags: {
      label: "Tags",
      description: "Select the tags",
    },
    roomType: {
      label: "Room Type",
      description: "Select the room type",
    },
    property: {
      label: "Property",
      description: "Select the property",
    },
    utmSources: {
      label: "UTM Sources",
      description: "",
    },
    utmMedium: {
      label: "UTM Medium",
      description: "",
    },
    attributionChannel: {
      label: "Attribution Channel",
      description: "",
    },
    standardMedium: {
      label: "Standard Medium",
      description: "",
    },
    standardSource: {
      label: "Standard Source",
      description: "",
    },
    deviceType: {
      label: "Device Type",
      description: "",
    },
    academicYear: {
      label: "Academic Year",
      description: "Select the academic year",
    },
    leadStage: {
      label: getClientCRMFlag("useContactLabel")
        ? "Contact Stage"
        : "Lead Stage",
      description: "Select the lead stage",
    },
    dealStage: {
      label: "Booking Stage",
      description: "Select the booking stage",
    },
    dealProperty: {
      label: "Property",
      description: "Select the booking property",
    },
    dealRoomType: {
      label: "Room Type",
      description: "Select the booking room type",
    },
    dealRoomNumber: {
      label: "Room Number",
      description: "Enter the booking room number",
    },
    dealAcademicYear: {
      label: "Academic Year",
      description: "Select the booking academic year",
    },
    dealAvailabilityTitle: {
      label: "Availability Title",
      description: "Select the booking availability title",
    },
    dealCity: {
      label: "City",
      description: "Select the booking city",
    },
    dealAssignee: {
      label: "Booking Owner",
      description: "Select the booking owner",
    },
    leadType: {
      label: "Lead Type",
      description: "Select the lead type",
    },
    viewings: {
      label: "Viewings",
      description: "",
    },
    excludeRepeatLeads: {
      label: "Exclude Repeat Leads",
      description: "",
    },
    bedCount: {
      label: "Bed Count",
      description: "Filter by bed count range",
    },
    propertyCount: {
      label: "Property Count",
      description: "Filter by number of properties",
    },
    activeFollowUps: {
      label: "Active Follow-Ups",
      description: "Leads with active follow-ups",
    },
    leadsNotUpdated30Days: {
      label: "Not Updated in 30 Days",
      description: "Excludes Opportunity & Lost stage leads",
    },
    lastActivity: {
      label: "Last Activity",
      description: "Filter leads by their last activity date",
    },
  },
  tasks: (isVerbafloClient = false) => ({
    dueToday: {
      label: isVerbafloClient ? "Follow-Ups Due Today" : "Tasks Due Today",
      description: isVerbafloClient
        ? "Show follow-ups due today"
        : "Show tasks due today",
    },
    overdue: {
      label: isVerbafloClient ? "Overdue Follow-Ups" : "Overdue Tasks",
      description: isVerbafloClient
        ? "Show overdue follow-ups"
        : "Show overdue tasks",
    },
    upcoming: {
      label: isVerbafloClient ? "Upcoming Follow-Ups" : "Upcoming Tasks",
      description: isVerbafloClient
        ? "Show upcoming follow-ups"
        : "Show upcoming tasks",
    },
    completed: {
      label: isVerbafloClient ? "Completed Follow-Ups" : "Completed Tasks",
      description: isVerbafloClient
        ? "Show completed follow-ups"
        : "Show completed tasks",
    },
    assignees: {
      label: isVerbafloClient ? "Follow-Up Assignees" : "Task Assignees",
      description: "Filter by assigned agents",
    },
  }),
  playground: {
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
  },
  playgroundLogs: {
    changesMadeOn: {
      label: "Changes Made On",
      description: "",
    },
    createdOnDate: {
      label: "Created on Date",
      description: "Select the duration",
    },
  },
  communicationAnalytics: {
    modeOfCommunication: {
      label: "Mode of Communication",
      description: "Select your preferred channel of communication",
    },
    duration: {
      label: "Duration",
      description: "Select the duration",
    },
  },
  viewings: {
    sort: {
      label: "Sort By",
      description: "Order viewings by date",
    },
    scheduledOnDate: {
      label: "Scheduled on",
      description: "",
    },
    createdOnDate: {
      label: "Created on",
      description: "",
    },
    property: {
      label: "Property",
      description: "Select the property",
    },
    attendeeResponse: {
      label: "User Response",
      description: "",
    },
  },
};

export const VIEWINGS_SORT_KEY_OPTIONS = [
  { value: "scheduled_at", displayValue: "Scheduled at" },
  { value: "created_at", displayValue: "Created at" },
  { value: "updated_at", displayValue: "Updated at" },
];

export const VIEWINGS_SORT_ORDER_OPTIONS = [
  { value: "desc", displayValue: "Newest first" },
  { value: "asc", displayValue: "Oldest first" },
];

export const getViewingsSortDisplayValue = (
  sortKey: string,
  sortOrder: string
): string => {
  if (!sortKey || !sortOrder) return "";
  const keyOption = VIEWINGS_SORT_KEY_OPTIONS?.find(
    (option) => option?.value === sortKey
  );
  const orderOption = VIEWINGS_SORT_ORDER_OPTIONS?.find(
    (option) => option?.value === sortOrder
  );
  if (!keyOption || !orderOption) return "";
  return `${keyOption?.displayValue} - ${orderOption?.displayValue}`;
};

export const getBudgetList = (clientRegion?: string) => {
  if (clientRegion === "US") {
    return [
      {
        label: "$200 - $400",
        min_budget: 200,
        max_budget: 400,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$400 - $600",
        min_budget: 400,
        max_budget: 600,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$600 - $800",
        min_budget: 600,
        max_budget: 800,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$800 - $1000",
        min_budget: 800,
        max_budget: 1000,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$1000 - $1200",
        min_budget: 1000,
        max_budget: 1200,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$1200 - $1400",
        min_budget: 1200,
        max_budget: 1400,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$1400 - $1600",
        min_budget: 1400,
        max_budget: 1600,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$1600 - $1800",
        min_budget: 1600,
        max_budget: 1800,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
      {
        label: "$1800 - $2000",
        min_budget: 1800,
        max_budget: 2000,
        duration: BudgetDuration.MONTHLY,
        currency: Currency.USD,
      },
    ];
  } else {
    return [
      {
        label: "£50 - £100",
        min_budget: 50,
        max_budget: 100,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£100 - £150",
        min_budget: 100,
        max_budget: 150,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£150 - £200",
        min_budget: 150,
        max_budget: 200,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£200 - £250",
        min_budget: 200,
        max_budget: 250,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£250 - £300",
        min_budget: 250,
        max_budget: 300,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£300 - £350",
        min_budget: 300,
        max_budget: 350,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£350 - £400",
        min_budget: 350,
        max_budget: 400,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£400 - £450",
        min_budget: 400,
        max_budget: 450,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
      {
        label: "£450 - £500",
        min_budget: 450,
        max_budget: 500,
        duration: BudgetDuration.WEEKLY,
        currency: Currency.GBP,
      },
    ];
  }
};

export const getBudgetInputPlaceholder = (
  clientRegion?: string,
  field?: "min" | "max"
) => {
  if (clientRegion === "US") {
    return field === "min" ? "$ Min" : "$ Max";
  } else {
    return field === "min" ? "£ Min" : "£ Max";
  }
};
